// In-memory + localStorage-backed mock store for running the app without Supabase.
// Schema mirrors the future Postgres tables 1:1 so swapping to real Supabase only
// touches the service layer.
//
// getAll() returns a stable reference per table; the cached array is only replaced
// when the table mutates. This is required for useSyncExternalStore to work without
// the React "infinite loop" snapshot warning.

import type {
  AuditLog,
  ConfigValue,
  Customer,
  DayCalendarEntry,
  ForecastAdjustment,
  ForecastResult,
  HistoricalOrder,
  ReviewDecision,
  Warehouse,
} from '@/domain';

export interface MockTables {
  warehouses: Warehouse[];
  customers: Customer[];
  day_calendar: DayCalendarEntry[];
  historical_orders: HistoricalOrder[];
  forecast_adjustments: ForecastAdjustment[];
  audit_log: AuditLog[];
  config_values: ConfigValue[];
  forecast_results: ForecastResult[];
  review_decisions: ReviewDecision[];
}

const STORAGE_KEY = 'wfp-v2:mock-store:v1';

const empty: MockTables = {
  warehouses: [],
  customers: [],
  day_calendar: [],
  historical_orders: [],
  forecast_adjustments: [],
  audit_log: [],
  config_values: [],
  forecast_results: [],
  review_decisions: [],
};

function load(): MockTables {
  if (typeof window === 'undefined') return structuredClone(empty);
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(empty);
    return { ...structuredClone(empty), ...(JSON.parse(raw) as Partial<MockTables>) };
  } catch {
    return structuredClone(empty);
  }
}

function persist(state: MockTables) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // happy-dom may not implement localStorage in some test contexts
  }
}

class MockStore {
  private state: MockTables = load();
  private listeners = new Set<() => void>();

  getAll<K extends keyof MockTables>(table: K): MockTables[K] {
    return this.state[table];
  }

  replace<K extends keyof MockTables>(table: K, rows: MockTables[K]) {
    this.state = { ...this.state, [table]: rows.slice() as MockTables[K] };
    persist(this.state);
    this.emit();
  }

  append<K extends keyof MockTables>(table: K, row: MockTables[K][number]) {
    const next = [...this.state[table], row] as MockTables[K];
    this.state = { ...this.state, [table]: next };
    persist(this.state);
    this.emit();
  }

  update<K extends keyof MockTables>(
    table: K,
    predicate: (row: MockTables[K][number]) => boolean,
    patch: Partial<MockTables[K][number]>,
  ) {
    const next = this.state[table].map((row) =>
      predicate(row as MockTables[K][number]) ? { ...row, ...patch } : row,
    ) as MockTables[K];
    this.state = { ...this.state, [table]: next };
    persist(this.state);
    this.emit();
  }

  reset() {
    this.state = structuredClone(empty);
    persist(this.state);
    this.emit();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.listeners.forEach((fn) => fn());
  }
}

export const mockStore = new MockStore();
